import { fetchApiJson, trackError } from '@vend/utilities'
import { FEATURE_ENABLE_INVOICING_PAYMENT_REQUESTS } from '../../constants'
import { getFeatureFromCache } from '../../util/feature-cache'

/**
 * Payment information for a payment request
 */
export interface PaymentRequestPayment {
  id: string
  amount: number
  capturedAt: string // Date when this payment was made
  status: string
}

/**
 * Payment Request response structure from invoicing API
 */
export interface PaymentRequestQueryResponse {
  id: string
  status: string
  requestedAmount: number
  processingBalance?: number
  currency: string
  location: {
    locationRef: string
    name: string
    email: string
  }
  note?: string
  documentRefs?: string[]
  shopRef?: string
  registerRef?: string
  accountRef?: string
  metadata?: Record<string, string>
  contactId?: string
  proposalHash: string
  createdAt: string // Date when payment request was created/sent
  payments?: PaymentRequestPayment[] // Array of payments made against this request
}

/**
 * API response structure with meta and data
 */
export interface FetchPaymentRequestsApiResponse {
  meta: {
    totalCount: number
    totalAmount: number
  }
  data: PaymentRequestQueryResponse[]
}

/**
 * Wrapper response for fetch operation
 */
export interface FetchPaymentRequestsResponse {
  success: boolean
  paymentRequests?: FetchPaymentRequestsApiResponse
  error?: string
}

/**
 * Optional filters for fetching payment requests
 */
export interface PaymentRequestFilters {
  status?: string
  customerRef?: string
  // Add other filters as needed
}

/**
 * Fetch payment requests for a given sale ID from the invoicing API
 *
 * @param saleId - The sale ID to fetch payment requests for
 * @param filters - Optional filters (status, customerRef)
 * @return Promise with success status, payment requests data, or error
 */
export const fetchPaymentRequestsForSale = async (
  saleId: string,
  filters?: PaymentRequestFilters
): Promise<FetchPaymentRequestsResponse> => {
  try {
    // Build query parameters
    const params = new URLSearchParams({ documentRef: saleId })

    if (filters?.status) {
      params.append('status', filters.status)
    }
    if (filters?.customerRef) {
      params.append('customerRef', filters.customerRef)
    }

    const response: FetchPaymentRequestsApiResponse = await fetchApiJson(
      `/api/2.0/invoicing/payment-requests?${params.toString()}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }
    )

    return {
      success: true,
      paymentRequests: response,
    }
  } catch (err: any) {
    return {
      success: false,
      error: err?.message || 'Failed to fetch payment requests',
    }
  }
}

/**
 * Check if sale has PaymentRequest attribute and fetch payment requests if it does
 * This should be called when:
 * - A sale is fetched/loaded
 * - User navigates to the payment panel
 * - Sale is refreshed
 *
 * @param saleId - The sale ID to fetch payment requests for
 * @param hasPaymentRequestAttribute - Whether the sale has the PaymentRequest attribute
 * @param filters - Optional filters (status, customerRef, etc.)
 * @return Promise with payment requests data, or null if attribute doesn't exist
 */
export const checkAndFetchPaymentRequests = async (
  saleId: string | null | undefined,
  hasPaymentRequestAttribute: boolean,
  filters?: PaymentRequestFilters
): Promise<FetchPaymentRequestsResponse | null> => {
  // Check if feature is enabled
  if (!getFeatureFromCache(FEATURE_ENABLE_INVOICING_PAYMENT_REQUESTS)) {
    return null
  }

  // Only fetch if sale has PaymentRequest attribute and saleId exists
  if (!hasPaymentRequestAttribute || !saleId) {
    return null
  }

  const result = await fetchPaymentRequestsForSale(saleId, filters)

  if (!result.success) {
    trackError(
      'Fetch Payment Requests Error',
      result.error || 'Unknown error',
      { saleId }
    )
  }

  return result
}
